import React, { useState, useEffect, useRef } from 'react';
import { GOOGLE_MAPS_API_KEY } from '../utils/config';
import './AddressSearch.css';

const AddressSearchInput = ({ onLocationSelect, initialValue = '', placeholder = 'Enter US address...', disabled = false }) => {
  const [inputValue, setInputValue] = useState(initialValue); // Initialize with prop
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    if (window.google && window.google.maps) return;

    if (scriptLoadedRef.current) return;
    scriptLoadedRef.current = true;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true; // Recommended for modern browsers
    script.onload = () => console.log('Google Maps script loaded');
    script.onerror = () => setError('Failed to load Google Maps script');
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    setInputValue(initialValue); // Update address state if initialValue changes
  }, [initialValue]);

  const getAddressFields = (components) => {
    const getComponent = (type, useShort = false) => components.find((c) => c.types.includes(type))?.[useShort ? 'short_name' : 'long_name'] || '';

    const streetNumber = getComponent('street_number');
    const route = getComponent('route');
    const streetAddress = [streetNumber, route].filter(Boolean).join(' ');

    const address = {
      road: streetAddress, // Aligned with OpenStreetMap 'road'
      city: getComponent('locality'),
      neighbourhood: getComponent('sublocality') || getComponent('neighborhood'), // Aligned with OpenStreetMap 'neighbourhood'
      county: getComponent('administrative_area_level_2'),
      state: getComponent('administrative_area_level_1'), // Use full state name to match OpenStreetMap
      postcode: getComponent('postal_code'), // Aligned with OpenStreetMap 'postcode'
    };
    // Return a new object to avoid potential mutation issues if the original components array is used elsewhere.
    return { ...address };
  };

  // This is the corrected version using the Maps JavaScript API's Geocoder
  const handleAddressChange = (e) => {
    const input = e.target.value;
    setInputValue(input);
    setError(null);

    if (!window.google || !window.google.maps) {
      setError('Google Maps script is not available yet.');
      return;
    }

    if (input.length > 2) {
      setIsLoading(true);
      const geocoder = new window.google.maps.Geocoder();
      const request = {
        address: input,
        language: 'en-US',
        region: 'us',
        componentRestrictions: {
          country: 'US',
        },
      };

      geocoder.geocode(request, (results, status) => {
        setIsLoading(false);
        if (status === 'OK') {
          // The results object is already in the correct format
          const streetAddresses = results.filter((result) => result.types.includes('street_address') || result.types.includes('premise') || result.types.includes('subpremise'));
          setSuggestions(streetAddresses.length > 0 ? streetAddresses : results.slice(0, 7));
        } else if (status === 'ZERO_RESULTS') {
          setSuggestions([]);
        } else {
          setError(`Geocoding API error: ${status}`);
          setSuggestions([]);
        }
      });
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion.formatted_address);
    setSuggestions([]);

    const geojsonFeature = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [suggestion.geometry.location.lng(), suggestion.geometry.location.lat()],
      },
      properties: {
        name: suggestion.formatted_address,
        place_id: suggestion.place_id,
        types: suggestion.types,
        address: getAddressFields(suggestion.address_components),
      },
    };

    if (onLocationSelect) {
      onLocationSelect(geojsonFeature);
    }
  };

  return (
    <div className="address-search-input-container google-search">
      <input type="text" value={inputValue} onChange={handleAddressChange} placeholder={placeholder} className="address-search-box" aria-label="Address Search Input" disabled={disabled} />
      {isLoading && <div className="address-search-loading">Loading...</div>}
      {error && <div className="address-search-error">{error}</div>}
      {suggestions.length > 0 && !disabled && (
        <ul className="address-suggestions-list">
          {suggestions.map((s) => (
            <li key={s.place_id} onClick={() => handleSuggestionClick(s)} className="address-suggestion-item">
              {s.formatted_address}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressSearchInput;
